import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { FormEvent, useLayoutEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import FormProvider from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import { PinnedDemandRequest } from '../../hooks/useDemandOperationKey';
import { OperationKeyHandle } from '../../hooks/useOperationKey';
import { CreateDemandBatchVariables } from '../../hooks/useWarehouseDemandMutations';
import UnconfirmedSaveBanner from '../UnconfirmedSaveBanner';
import {
  DemandProductOption,
  DemandRequestFormValues,
  buildDemandBatchPayload,
  buildDemandRequestDefaults,
  demandRequestFormResolver,
} from './WarehouseDemandRequestForm.schema';
import WarehouseDemandRequestFields from './WarehouseDemandRequestFields';

const f = (key: string): string => translateKey(`warehouse.demand.form.${key}`);

/** D-3: the frozen request and the form it was built from. */
interface PinnedDemand extends PinnedDemandRequest {
  values: DemandRequestFormValues;
}

export interface WarehouseDemandRequestFormModalProps {
  isOpen: boolean;
  /** Read only when the form opens; opening again while open keeps the form. */
  initialProduct: DemandProductOption | null;
  /** Read only when the form opens: a request for another product by this name (#690). */
  initialName?: string | null;
  /** The launcher's clinic: the series' organization, pinned with each request. */
  organizationId: string;
  /** `createBatch.isLoading`. */
  isSubmitting: boolean;
  /**
   * D-3: the launcher's scoped series (`useDemandOperationKey`). It outlives
   * this dialog and its host; the form never creates or ends a series itself.
   */
  operationKeys: OperationKeyHandle;
  /**
   * F-8: the series' last attempt was refused with 422 WAREHOUSE_DISABLED.
   * The key stays held, but that attempt saved nothing, so the banner says
   * the Warehouse is off.
   */
  blocked: boolean;
  onClose: () => void;
  /** The launcher's batch create (`mutateAsync`): the form settles the key with its outcome. */
  onSubmit: (variables: CreateDemandBatchVariables) => Promise<unknown>;
}

/**
 * "Zgłoś zapotrzebowanie": several products, each its own Demand Request,
 * sent once as one batch with the series' operation key (D-3). After an
 * ambiguous outcome the form shows the frozen batch, locked, and "Ponów
 * zapis" resends it with the same key, so a lost answer never raises it (and
 * emails the admins) twice. The launcher closes the form once it is saved.
 */
export default function WarehouseDemandRequestFormModal({
  isOpen,
  initialProduct,
  initialName = null,
  organizationId,
  isSubmitting,
  operationKeys,
  blocked,
  onClose,
  onSubmit,
}: WarehouseDemandRequestFormModalProps): JSX.Element {
  const methods = useForm<DemandRequestFormValues>({
    resolver: demandRequestFormResolver,
    defaultValues: buildDemandRequestDefaults(initialProduct, initialName),
  });
  const { control, handleSubmit, reset } = methods;
  const noLines = useWatch({ control, name: 'lines' }).length === 0;
  const wasOpen = useRef(false);
  // The next save resends the frozen request, so the form shows it, locked,
  // until the series ends.
  const locked = operationKeys.pending || isSubmitting;

  // Only opening resets the form; a second open while it is open (a late
  // name lookup, another entry point) or a settle never wipes it. Before
  // paint, so a reopened form never shows one frame of the previous values.
  useLayoutEffect(() => {
    if (isOpen && !wasOpen.current) {
      const frozen = operationKeys.pending
        ? operationKeys.peek<PinnedDemand>()
        : undefined;
      reset(
        frozen?.values ??
          buildDemandRequestDefaults(initialProduct, initialName),
      );
    }
    wasOpen.current = isOpen;
  }, [isOpen, initialProduct, initialName, operationKeys, reset]);

  const close = (): void => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const save = async (values: DemandRequestFormValues): Promise<void> => {
    // Refused before a series exists: without a clinic nothing can be sent,
    // and a request on its way is answered first.
    if (!organizationId || isSubmitting) {
      return;
    }
    // This attempt's key: its answer settles only its own series.
    const key = operationKeys.current();
    try {
      // After an ambiguous outcome (no response, 408, 429, 5xx) the same key
      // and the same body are resent, so a retry never raises it twice.
      const { payload } = operationKeys.request<PinnedDemand>(() => ({
        values,
        payload: {
          organizationId,
          dto: { operationKey: key, ...buildDemandBatchPayload(values) },
        },
      }));
      await onSubmit(payload);
      operationKeys.settle(undefined, key);
    } catch (error) {
      // The shared rule keeps or ends the series; the mutation already told
      // the user about a definitive refusal.
      operationKeys.settle(error, key);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      fullWidth
      PaperProps={{
        // Lifts the theme's 640px cap: product names run long.
        sx: {
          width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 48px)' },
          maxWidth: '800px !important',
          mx: 0,
        },
      }}
    >
      {/* React bubbles submit through portals; the medication stepper is a form. */}
      <Box onSubmit={(event: FormEvent) => event.stopPropagation()}>
        <FormProvider methods={methods} onSubmit={handleSubmit(save)}>
          <DialogTitle>{f('title')}</DialogTitle>
          <DialogContent>
            <UnconfirmedSaveBanner
              pending={operationKeys.pending}
              message={
                blocked
                  ? translateKey('warehouse.errors.WAREHOUSE_DISABLED')
                  : f('unconfirmedSave')
              }
              testId="warehouse-demand-unconfirmed"
            />
            <WarehouseDemandRequestFields isOpen={isOpen} disabled={locked} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={close}
              disabled={isSubmitting}
              data-testid="warehouse-demand-cancel"
            >
              {translateKey('warehouse.common.cancel')}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              // A frozen batch always has its lines; a new one needs one.
              disabled={noLines && !operationKeys.pending}
              data-testid="warehouse-demand-submit"
            >
              {f(operationKeys.pending ? 'retrySubmit' : 'submit')}
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Box>
    </Dialog>
  );
}
